import { useEffect, useState } from 'react';

import { MenuOutlined } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { doAddNewAccountType, doClearSwitchAccount, doSucceededGetAccount, doSwitchAccount } from 'src/actions';
import { NavigatePathConstant } from 'src/constants';
import {
  useAccountSelector,
  useAuthenticatedUserSelector,
  useCanCreateAccount,
  useCanSwitchAccount,
  useFindOtherAccountList,
} from 'src/hooks';
import { Account, AccountStatus } from 'src/models';
import { ColorConstant, Spacing } from 'src/styles';
import { Maybe } from 'src/utils';

import { logo } from '../../styles/images';
import { MDivider } from '../MDivider/MDivider';
import { MDrawer } from '../MDrawer/MDrawer';

import { AccountMenuItem } from './AccountMenuItem';
import { AccountStatusBanner } from './AccountStatusBanner';
import { AccountValue } from './AccountValue';
import { getAccountSettingMenuItemList, getUserSettingMenuItemList } from './constants';
import * as Styles from './Header.styles';
import './index.css';
import { LogoutMenuItem } from './LogoutMenuItem';
import { MenuItem } from './MenuItem';
import { UntilYourEntityAccountModal } from './UntilYourEntityAccountModal';
import { UserInfo } from './UserInfo';

export type MobileHeaderProps = {
  authToken?: string;
  status?: AccountStatus;
};

export const MobileHeader = ({ authToken, status }: MobileHeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSwitchLoading: boolean = useSelector((state: any) => state.accounts.switch.isLoading);
  const isSwitchSuccess: boolean = useSelector((state: any) => Boolean(state.accounts.switch.__succeeded));
  const switchAccount: Maybe<Account> = useSelector((state: any) => state.accounts.switch.data);

  const [switchToAccountId, setSwitchToAccountId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [step, setStep] = useState<'account' | 'switch'>('account');
  const [untilYourAccountModalIsOpen, setIsUntilYourAccountModalIsOpen] = useState(false);

  const { isUserLoading } = useAuthenticatedUserSelector();
  const { account } = useAccountSelector();
  const canCreateAccount = useCanCreateAccount();
  const canSwitchAccount = useCanSwitchAccount();
  const otherAccountList = useFindOtherAccountList();

  const onCreateAccount = () => {
    if (account?.type.isEntity && !account.isEntityAccountTypeActive && account.isRoot) {
      setIsUntilYourAccountModalIsOpen(true);

      return;
    }

    dispatch(doAddNewAccountType());
    navigate('/accounts/personal-information');
  };

  const onGoToSwitchStep = () => {
    setStep('switch');
  };

  const onGoBackToAccountStep = () => {
    setStep('account');
  };

  const switchTo = (account: Account) => {
    setSwitchToAccountId(account.id);
    dispatch(doSwitchAccount({ params: { id: account.id } }));
  };

  const renderAccountSettingMenuItemList = () => {
    return (
      <Col span={24}>
        {getAccountSettingMenuItemList(account).map(aMenuItem => (
          <MenuItem
            key={aMenuItem.key}
            name={aMenuItem.name}
            path={aMenuItem.path(account?.id)}
            startIcon={aMenuItem.startIcon?.()}
            endIcon={aMenuItem.endIcon?.(status)}
            activatable={false}
          />
        ))}
      </Col>
    );
  };

  const renderUserSettingMenuItemList = () => {
    return (
      <Col span={24}>
        {getUserSettingMenuItemList(account).map(aMenuItem => (
          <MenuItem key={aMenuItem.key} name={aMenuItem.name} path={aMenuItem.path(account?.id)} />
        ))}
      </Col>
    );
  };

  const renderResourceLinkList = () => {
    if (step === 'switch') return null;

    return (
      <Row>
        <Col span={24}>
          <MDivider />
        </Col>
        <Col span={24}>
          <MenuItem name='About' path='/about' activatable={false} />
        </Col>
        {authToken ? null : (
          <Col span={24}>
            <MenuItem name='Offers' path='/offers' activatable={false} />
          </Col>
        )}
        <Col span={24}>
          <MenuItem name='FAQ' path='/faq' activatable={false} />
        </Col>
      </Row>
    );
  };

  const renderLogout = () => {
    return authToken ? (
      <Row>
        <Col span={24}>
          <MDivider />
        </Col>
        <Col span={24}>
          <LogoutMenuItem onClick={() => setIsDrawerOpen(false)} />
        </Col>
      </Row>
    ) : null;
  };

  const renderOtherAccountList = () => {
    if (isEmpty(otherAccountList)) return null;

    return (
      <>
        <Row>
          <MDivider />
        </Row>
        <Row>
          <Col span={24}>
            <span className={Styles.menuSubtitle}>OTHER ACCOUNTS</span>
          </Col>
          {otherAccountList.map(acc => (
            <Col span={24} key={acc.id}>
              <AccountMenuItem
                account={acc}
                onClick={() => switchTo(acc)}
                isLoading={switchToAccountId === acc.id && (isSwitchLoading || isUserLoading)}
              />
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const renderAccountStep = () => {
    return (
      <Row>
        <Col span={24}>
          <UserInfo />
        </Col>
        {account?.accountId && (
          <Col span={24} className={Spacing.mb24}>
            <AccountValue />
          </Col>
        )}
        <Col span={24}>
          <MenuItem
            name='My Account'
            path={
              account?.isEntityAccountTypeActive ? '/accounts/entity-information' : '/accounts/personal-information'
            }
            activatable={false}
          />
        </Col>
        {canSwitchAccount && (
          <Col span={24}>
            <MenuItem
              name='Switch Account'
              endIcon={<i className='ri-arrow-right-s-line' style={{ fontSize: 24 }} />}
              activatable={false}
              onClick={onGoToSwitchStep}
            />
          </Col>
        )}
        {canCreateAccount && (
          <Col span={24}>
            <MenuItem
              startIcon={<i className='ri-user-add-line' style={{ fontSize: 24 }} />}
              name='Add New Account Type'
              path='/accounts/personal-information'
              activatable={false}
              onClick={onCreateAccount}
            />
          </Col>
        )}
        {account?.accountId && (
          <>
            <Col span={24}>
              <MDivider />
            </Col>
            <Col span={24}>
              <span className={Styles.menuSubtitle}> ACCOUNT SETTINGS</span>
              {renderAccountSettingMenuItemList()}
            </Col>
          </>
        )}
        <Col span={24}>
          <MDivider />
        </Col>
        <Col span={24}>
          <span className={Styles.menuSubtitle}> USER SETTINGS</span>
          {renderUserSettingMenuItemList()}
        </Col>
        <Col span={24}>
          <MDivider />
        </Col>
        <Col span={24}>
          <MenuItem
            name='Summary'
            path={`/${NavigatePathConstant.MAIN}/${NavigatePathConstant.SUMMARY}`}
            activatable={false}
          />
        </Col>
        <Col span={24}>
          <MenuItem name='Offers' path='/offers' activatable={false} />
        </Col>
        <Col span={24}>
          <MenuItem
            name='History'
            path={`/${NavigatePathConstant.MAIN}/${NavigatePathConstant.HISTORY}`}
            activatable={false}
          />
        </Col>
      </Row>
    );
  };

  const renderSwitchStep = () => {
    return (
      <>
        <Row>
          <Col span={24}>
            <span className={Styles.menuSubtitle}>CURRENT ACCOUNT</span>
          </Col>
          <Col span={24}>
            <AccountMenuItem account={account} onClick={onGoBackToAccountStep} />
          </Col>
        </Row>
        {renderOtherAccountList()}
      </>
    );
  };

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location]);

  useEffect(() => {
    if (isSwitchSuccess && switchAccount) {
      dispatch(doSucceededGetAccount(switchAccount));
      navigate('/main/summary');
    }

    return () => {
      dispatch(doClearSwitchAccount());
    };
  }, [isSwitchSuccess]);

  return (
    <div>
      <Row justify='center'>
        <Col span={24}>
          <div className='mobileHeader'>
            <Link to='/'>
              <img className='logo' src={logo} alt='My IPO' />
            </Link>
            <Button
              icon={<MenuOutlined style={{ fontSize: '20px', color: ColorConstant.BRAND6 }} />}
              onClick={() => setIsDrawerOpen(true)}
              className='mobileNavBtn'
            />
            <MDrawer
              closeIcon={
                step === 'account' ? undefined : (
                  <span>
                    <i className='ri-arrow-left-line' />
                  </span>
                )
              }
              isOpen={isDrawerOpen}
              onClose={() => {
                if (step === 'switch') {
                  setStep('account');

                  return;
                }
                setIsDrawerOpen(false);
              }}
              title={step === 'account' ? undefined : 'Accounts'}>
              {!authToken ? (
                <Row>
                  <Col span={24}>
                    <MenuItem name='Login' path='/login' activatable={false} />
                  </Col>
                  <Col span={24}>
                    <MenuItem name='Register' path='/register' activatable={false} />
                  </Col>
                </Row>
              ) : (
                <>{step === 'account' ? renderAccountStep() : renderSwitchStep()}</>
              )}
              {renderResourceLinkList()}
              {renderLogout()}
            </MDrawer>
          </div>
        </Col>
        <AccountStatusBanner />
      </Row>
      <UntilYourEntityAccountModal
        open={untilYourAccountModalIsOpen}
        onClose={() => setIsUntilYourAccountModalIsOpen(false)}
      />
    </div>
  );
};
